// FILE: src/components/case-studies/admin/caseAdminStyles.js
// Shared inline-style tokens for the case-study admin surfaces. Values mirror
// src/styles/theme.css (the Study Plan reference palette) so the authoring
// screens sit inside the established PM Pixel visual language.

export const T = {
  bg: 'var(--bg, #f6f7fb)',
  panel: 'var(--panel, #ffffff)',
  ink: 'var(--ink, #1a2138)',
  muted: 'var(--muted, #5a6582)',
  line: 'var(--line, #e5e8f1)',
  lineStrong: 'var(--line-strong, #cfd5e6)',
  brand: 'var(--brand, #2c63ff)',
  brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)',
  goodSoft: 'var(--good-soft, #e6f7ef)',
  warn: 'var(--warn, #e9a23b)',
  warnSoft: 'var(--warn-soft, #fff4dc)',
  bad: 'var(--bad, #d64545)',
  badSoft: 'var(--bad-soft, #fde9ea)',
  shadow: 'var(--shadow, 0 1px 0 rgba(20,30,60,0.04), 0 2px 6px rgba(20,30,60,0.05))',
  radius: 'var(--radius, 12px)',
  radiusSm: 'var(--radius-sm, 8px)',
};

export const S = {
  page: { background: T.bg, minHeight: '100vh', color: T.ink, padding: '18px 22px 80px',
          fontFamily: "'Segoe UI', system-ui, sans-serif" },
  panel: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: T.radius,
           boxShadow: T.shadow, padding: 16 },
  h1: { fontSize: '1.05rem', fontWeight: 800, margin: '0 0 4px' },
  // First row of a full-width admin page. The HeaderUserMenu is FIXED at the
  // viewport's top-right (~240px wide, z-index 900), so right-aligned actions
  // on this row (e.g. the editor's Publish button) slide underneath it unless
  // the row reserves that corner (Bryan, 2026-07-12).
  headerRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
               flexWrap: 'wrap', paddingRight: 260 },
  sub: { color: T.muted, fontSize: '0.82rem' },
  btn: { padding: '7px 13px', borderRadius: T.radiusSm, border: `1px solid ${T.lineStrong}`,
         background: T.panel, color: T.ink, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' },
  btnPrimary: { padding: '7px 13px', borderRadius: T.radiusSm, border: `1px solid ${T.brand}`,
                background: T.brand, color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },
  btnDanger: { padding: '7px 13px', borderRadius: T.radiusSm, border: `1px solid ${T.bad}`,
               background: T.panel, color: T.bad, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },
  btnGhost: { padding: '3px 8px', borderRadius: 6, border: `1px solid ${T.line}`,
              background: 'transparent', color: T.muted, fontSize: '0.74rem', cursor: 'pointer' },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: T.radiusSm,
           border: `1px solid ${T.lineStrong}`, fontSize: '0.85rem', color: T.ink, background: T.panel },
  label: { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: T.muted,
           textTransform: 'uppercase', letterSpacing: '0.4px', margin: '10px 0 4px' },
  chip: (bg, fg) => ({ display: 'inline-block', padding: '2px 9px', borderRadius: 999,
                       fontSize: '0.7rem', fontWeight: 700, background: bg, color: fg }),
  modalScrim: { position: 'fixed', inset: 0, background: 'rgba(13,23,51,0.45)', zIndex: 90,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: T.panel, borderRadius: T.radius, boxShadow: T.shadow, padding: 20,
           width: 'min(640px, 94vw)', maxHeight: '86vh', overflowY: 'auto' },
  err: { background: T.badSoft, border: `1px solid ${T.bad}`, color: T.bad,
         borderRadius: T.radiusSm, padding: '8px 12px', fontSize: '0.82rem', margin: '8px 0' },
};

export function statusChip(status) {
  const map = {
    draft: [T.warnSoft, '#8a6116'], review: [T.brandSoft, T.brand],
    approved: [T.goodSoft, T.good], published: [T.good, '#fff'],
    archived: [T.line, T.muted],
  };
  const [bg, fg] = map[status] || [T.line, T.muted];
  return { ...S.chip(bg, fg), textTransform: 'capitalize' };
}
